// Trust badge — separate concept from seniority. Reads `provider_profiles.trust_status`.
// See docs/glossary.md.
import { ShieldAlert, ShieldQuestion, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export type TrustStatus = "not_verified" | "pending" | "verified";

const SIZE_MAP = {
  sm: { px: "px-1.5", py: "py-[1px]", text: "text-[9px]", icon: 10 },
  md: { px: "px-2", py: "py-[2px]", text: "text-[10px]", icon: 11 },
  lg: { px: "px-2.5", py: "py-[3px]", text: "text-xs", icon: 13 },
} as const;

const STYLES: Record<
  TrustStatus,
  { bg: string; fg: string; Icon: typeof ShieldCheck; labelKey: string; tooltipKey: string }
> = {
  not_verified: {
    bg: "hsl(var(--trust-none))",
    fg: "hsl(var(--trust-none-fg))",
    Icon: ShieldAlert,
    labelKey: "trust.notVerified.label",
    tooltipKey: "trust.notVerified.tooltip",
  },
  pending: {
    bg: "hsl(var(--trust-pending))",
    fg: "hsl(var(--trust-pending-fg))",
    Icon: ShieldQuestion,
    labelKey: "trust.pending.label",
    tooltipKey: "trust.pending.tooltip",
  },
  verified: {
    bg: "hsl(var(--trust-verified))",
    fg: "hsl(var(--trust-verified-fg))",
    Icon: ShieldCheck,
    labelKey: "trust.verified.label",
    tooltipKey: "trust.verified.tooltip",
  },
};

export function TrustBadge({
  status,
  size = "md",
  className,
}: {
  status: TrustStatus | null | undefined;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const { t } = useTranslation();
  if (!status) return null;
  const s = STYLES[status];
  const sz = SIZE_MAP[size];
  const label = t(s.labelKey);
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="status"
            aria-label={label}
            className={cn(
              "inline-flex items-center gap-1 rounded-full font-bold uppercase tracking-wider",
              sz.px,
              sz.py,
              sz.text,
              className,
            )}
            style={{ background: s.bg, color: s.fg }}
          >
            <s.Icon size={sz.icon} aria-hidden />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-[240px] text-xs">{t(s.tooltipKey)}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}